import { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getConsent } from '../../../sdk/services/contentService';
import { AccountDataModel } from '../../../sdk/type';
import amwayLogo from '../../../assets/image/Amw_Logo_Black.webp';
import { LoggerService } from '../../utils';
import { Button, ButtonStyles, ButtonTypes, Checkbox, Modal, RadioButton, TextAlign, Theme } from '../../components';
import styles from './SecurityPrivacy.module.scss';

interface SystemSupportProps {
  invalidType: InvalidType;
  onExit: Function;
}

interface SecurityPrivacyProps {
  onAgree: Function;
  onCancel: Function;
}

interface UserAccountSelectionProps {
  accounts: AccountDataModel[];
  onConfirm: Function;
  onBack: Function;
}

export type InvalidType = 'invalidAff' | 'invalidSalesPlan' | 'invalidStatus' | 'invalidToken' | null;

export const SystemSupportModal = ({ invalidType, onExit }: SystemSupportProps) => {
  const { t } = useTranslation();
  return (
    <Modal
      className={styles['system-support']}
      headline={''}
      closeIcon={false}
      primaryButton={
        <Button
          title={t('systemSupport.exit')}
          type={ButtonTypes.BUTTON}
          onClick={() => onExit()}
          buttonStyle={ButtonStyles.SOLID}
          theme={Theme.GREEN}
        />
      }
    >
      <>
        <p>{invalidType === 'invalidAff' ? t('systemSupport.desc.invalidAff') : null}</p>
        <p>{invalidType === 'invalidSalesPlan' ? t('systemSupport.desc.invalidSalesPlan') : null}</p>
        <p>{invalidType === 'invalidStatus' ? t('systemSupport.desc.invalidStatus') : null}</p>
        <p>{invalidType === 'invalidToken' ? t('systemSupport.desc.invalidToken') : null}</p>
      </>
    </Modal>
  );
};

export const SecurityPrivacy = ({ onAgree, onCancel }: SecurityPrivacyProps) => {
  const { t } = useTranslation();
  const [check, setCheck] = useState(false);
  const [acknowledgeCheck, setAcknowledgeCheck] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const data = await getConsent('', false);

        if (typeof data === 'object' && data !== null) {
          setPrivacyPolicy(data.privacy_and_policy ?? '');
          setTermsAndConditions(data.terms_and_conditions ?? '');
          let date = moment(data.date).format('MMMM D, YYYY');
          setDate(date ?? '');
        }
      } catch (error) {
        LoggerService.log('fetchPrivacyPolicy: Failed to retrieve content data.');
      }
    };
    fetchPrivacyPolicy();
  }, []);

  return (
    <Modal
      className={styles['security-privacy']}
      headline={
        <div className={styles['logo-container']}>
          <img className={styles['logo']} src={amwayLogo} alt="amway logo" height={30} />
        </div>
      }
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      primaryButton={
        <Button
          title={t('securityPrivacy.iagree')}
          type={ButtonTypes.BUTTON}
          onClick={() => onAgree()}
          buttonStyle={ButtonStyles.SOLID}
          theme={Theme.GREEN}
          disabled={check === false || acknowledgeCheck === false}
        />
      }
      secondaryButtonLabel={t('securityPrivacy.cancel')}
      secondaryButtonTheme={Theme.GREEN}
      onSecondaryButtonClick={() => onCancel()}
    >
      <div className={styles['security-privacy-container']}>
        <h1 className={styles['security-privacy-header']}>{t('securityPrivacy.title')}</h1>
        <article className={styles['security-privacy-box']}>
          <h3 className={styles['security-privacy-box-header']}>
            <strong>{t('securityPrivacy.terms-condition')}</strong>
          </h3>
          <p className={styles['security-privacy-box-date']}>
            <strong>{t('securityPrivacy.lastRevised').replace('{date}', date)}</strong>
          </p>
          <p dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
          <p dangerouslySetInnerHTML={{ __html: privacyPolicy }} />
        </article>
        <div className={styles['security-privacy-agreement']}>
          <p
            dangerouslySetInnerHTML={{
              __html: t('securityPrivacy.notes'),
            }}
          />
          <div style={{ display: 'block' }}>
            <Checkbox
              text={
                <p
                  id="agree-checkbox"
                  dangerouslySetInnerHTML={{
                    __html: t('securityPrivacy.agreement')
                      .replace('{terms-conditions}', `${t('securityPrivacy.terms-condition')}`)
                      .replace('{privacy-policy}', `${t('securityPrivacy.privacy-policy')}`),
                  }}
                  onClick={() => setCheck(prevValue => !prevValue)}
                />
              }
              onChange={() => {
                setCheck(prevValue => !prevValue);
              }}
              checked={check}
              className={styles['security-privacy-checkbox']}
              id={'agree-checkbox'}
            />
            <Checkbox
              text={
                <p
                  id="agree-acknowledge-checkbox"
                  dangerouslySetInnerHTML={{
                    __html: t('securityPrivacy.agreementAcknowledge')
                      .replace('{terms-conditions}', `${t('securityPrivacy.terms-condition')}`)
                      .replace('{privacy-policy}', `${t('securityPrivacy.privacy-policy')}`),
                  }}
                  onClick={() => setAcknowledgeCheck(prevValue => !prevValue)}
                />
              }
              onChange={() => setAcknowledgeCheck(prevValue => !prevValue)}
              checked={acknowledgeCheck}
              className={styles['security-privacy-checkbox']}
              id={'agree-acknowledge-checkbox'}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const UserSelection = ({ accounts, onBack, onConfirm }: UserAccountSelectionProps) => {
  const { t } = useTranslation();
  const [check, setCheck] = useState(0);

  return (
    <Modal
      className={styles['account-selection']}
      headline={<div className={styles['account-selection-header']}>{t('accountSelection.title')}</div>}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      primaryButtonLabel={t('accountSelection.confirm')}
      secondaryButtonLabel={t('accountSelection.back')}
      onPrimaryButtonClick={() => onConfirm(accounts[check])}
      onSecondaryButtonClick={() => onBack()}
    >
      <div className={styles['account-selection-container']}>
        <p>{t('accountSelection.description')}</p>
        <div className={styles['account-selection-accounts']}>
          {accounts.map((acc, idx) => (
            <RadioButton key={idx} label={acc.name} checked={check === idx} onChange={() => setCheck(idx)} />
          ))}
        </div>
      </div>
    </Modal>
  );
};
